import { create } from "zustand";
import { DEFAULT_FOOTSHOE_COLOR, DEFAULT_LIFTEDMESH_COLOR, DEFAULT_MESH_COLOR, DEFAULT_BOTTOM_MESH_COLOR, DEFAULT_TOP_MESH_COLOR, MeasurementSystem } from '../components/Viewer3d/consts';


export enum ViewerTabsMode {
    // None = 'NONE',
    View = 'VIEW',
    Additions = 'ADDITIONS',
    Edit = 'EDIT',
    Engraving = 'ENGRAVING',
    GCode = 'GCODE',
    Project = 'PROJECT',
}

interface ViewerStore {
    mode: ViewerTabsMode;
    previousViewerMode: ViewerTabsMode;
    grids: boolean;
    wireframe: string;
    perspectiveCamera: boolean;
    clipping: boolean;
    clippingVerticalPercent: number;
    clippingLateralPercent: number;
    bandHeightClipping: boolean;
    feetView: 'both' | 'right' | 'left';
    pressureTransparency: number;
    liftedInsoleTransparency: number;
    shoeDataTransparency: number;
    bonesTransparency: number;
    insolesVisible: boolean;
    gcodeVisible: boolean,
    additionsViewMeshVisible: boolean;
    liftedInsoleMeshVisible: boolean;
    bonesMeshVisible: boolean;
    footMeshVisible: boolean;
    shoeDataMeshVisible: boolean;
    highHeelMeshVisible: boolean;
    topInsoleDefaultColor: string;
    bottomInsoleDefaultColor: string;
    liftedInsoleDefaultColor: string;
    shoeDataDefaultColor: string;
    advanceMode: boolean;
    selectedAdditionFromViewer: { foot: number, additionIndex: number };
    alignPressure: boolean;
    alignFoot: boolean;
    gcodeStatus: { percent: number, result: string };
    markerPosition: { x: string, y: string, z: string } | null;
    showMarkerPosition: boolean;
    coloring: boolean;
    isOpenCVReady: boolean;
    updateFromViewer: boolean;
    updateFromPanel: boolean;
    surfaceUpdated: boolean;
    pressureMaxLabel: number;
    newDesign: boolean;
    oldLiveMode : boolean; // used to keep live mode status before toggling pressure alignment
    liveMode : boolean;
    voiceSessionActive: boolean;
    linkMode: boolean;
    animatePressure: boolean;
    measurementSystem: MeasurementSystem;

    setMode: (mode: ViewerTabsMode) => void;
    setPreviousViewerMode: (mode: ViewerTabsMode) => void;
    toggleGrids: () => void;
    setGrids: (val: boolean) => void;
    cycleWireframe: () => void;
    setWireframe: (val: string) => void;
    setPerspectiveCamera: (perspective: boolean) => void;
    toggleClipping: () => void;
    setClipping: (val: boolean) => void;
    setClippingPercent: (axis: "x" | "z", percent: number) => void;
    setClippingPercents: (vertical?: number, lateral?: number) => void;
    toggleBandHeightClipping: () => void;
    setBandHeightClipping: (val: boolean) => void;
    cycleFeetView: () => void;
    setFeetView: (val: string) => void;
    toggleInsolesVisible: () => void;
    toggleGCodeVisible: () => void;
    toggleAdditionsViewMeshVisible: () => void;
    toggleLiftedInsoleMeshVisible: () => void;
    toggleBonesMeshVisible: () => void;
    toggleFootMeshVisible: () => void;
    toggleShoeDataMeshVisible: () => void;
    toggleHighHeelMeshVisible: () => void;
    setPressureTrancparency: (val: number) => void;
    setLiftedInsoleTransparency: (val: number) => void;
    setShoeDataTransparency: (val: number) => void;
    setBonesTransparency: (val: number) => void;
    setTopInsoleDeafultColor: (val: string) => void;
    setBottomInsoleDeafultColor: (val: string) => void;
    setLiftedInsoleDefaultColor: (val: string) => void;
    setShoeDataDefaultColor: (val: string) => void;
    toggleAdvance: () => void;
    setGcodeStatus: (percent: number, result: string) => void;
    setMarkerPosition: (position: { x: string, y: string, z: string } | null) => void;
    setShowMarkerPosition: (val: boolean) => void;
    toggleAlignPressure: () => void;
    setAlignFoot: (alignFoot: boolean) => void;
    toggleColoring: () => void;
    setColoring: (val: boolean) => void;
    setOpenCVReady: (isReady: boolean) => void;
    setSelectedAdditionFromViewer: (foot: number, additionIndex: number) => void;
    setUpdateFromViewer: (value: boolean) => void;
    setUpdateFromPanel: (value: boolean) => void;
    toggleSurfaceUpdated: () => void;
    toggleLiveMode:() => void;
    setOldLiveMode:(val: boolean) => void;
    setLiveMode:(val: boolean) => void;
    setVoiceSessionActive:(val: boolean) => void;
    toggleLinkMode:() => void;
    toggleAnimatePressure: () => void;
    setMeasurementSystem: (unit: MeasurementSystem) => void;
}

export const useViewerStore = create<ViewerStore>((set) => ({
    mode: ViewerTabsMode.View,
    previousViewerMode: ViewerTabsMode.View,
    grids: false,
    wireframe: 'insole',
    perspectiveCamera: true,
    clipping: false,
    clippingVerticalPercent: 50,
    clippingLateralPercent: 100,
    bandHeightClipping: false,
    feetView: 'both',
    pressureTransparency: 255,
    liftedInsoleTransparency: 255,
    bonesTransparency: 255,
    shoeDataTransparency: 128,
    insolesVisible: true,
    gcodeVisible: false,
    additionsViewMeshVisible: true,
    liftedInsoleMeshVisible: true,
    bonesMeshVisible: true,
    footMeshVisible: true,
    shoeDataMeshVisible: true,
    highHeelMeshVisible: true,
    MeshVisible: true,
    topInsoleDefaultColor: DEFAULT_TOP_MESH_COLOR.getHexString(),
    bottomInsoleDefaultColor: DEFAULT_BOTTOM_MESH_COLOR.getHexString(),
    liftedInsoleDefaultColor: DEFAULT_LIFTEDMESH_COLOR.getHexString(),
    shoeDataDefaultColor: DEFAULT_FOOTSHOE_COLOR.getHexString(),
    advanceMode: false,
    selectedAdditionFromViewer: { foot: 0, additionIndex: 0 },
    gcodeStatus: { percent: 0, result: 'idle' },
    markerPosition: null,
    showMarkerPosition: false,
    alignPressure: false,
    alignFoot: false,
    coloring: false,
    updateFromViewer: false,
    updateFromPanel: false,
    surfaceUpdated: false,
    isOpenCVReady: false,
    pressureMaxLabel: 1.1,
    newDesign: false,
    oldLiveMode: false,
    liveMode: false,
    voiceSessionActive: false,
    linkMode: true,
    animatePressure: false,
    measurementSystem: MeasurementSystem.metric,

    setMeasurementSystem: (unit: MeasurementSystem) => {
        set({ measurementSystem: unit });
    },
    setMode: (mode: ViewerTabsMode) => set({ mode }),
    setPreviousViewerMode: (mode: ViewerTabsMode) => set({ previousViewerMode: mode }),
    toggleGrids: () =>
        set((state) => ({ grids: !state.grids })),
    setGrids: (val: boolean) => set({ grids: val }),

    setPerspectiveCamera: (perspective: boolean) =>
        set({ perspectiveCamera: perspective }),

    toggleClipping: () =>
        set((state) => ({ clipping: !state.clipping })),
    setClipping: (val: boolean) => set({ clipping: val }),
    toggleBandHeightClipping: () =>
        set((state) => ({ bandHeightClipping: !state.bandHeightClipping })),
    setBandHeightClipping: (val: boolean) => set({ bandHeightClipping: val }),
    setClippingPercent: (axis, percent) => {
        const value = Math.max(0, Math.min(100, Math.round(percent)));
        if (axis === "z") set({ clippingVerticalPercent: value });
        else set({ clippingLateralPercent: value });
    },
    setClippingPercents: (vertical, lateral) =>
        set((state) => ({
            clippingVerticalPercent: vertical == null
                ? state.clippingVerticalPercent
                : Math.max(0, Math.min(100, Math.round(vertical))),
            clippingLateralPercent: lateral == null
                ? state.clippingLateralPercent
                : Math.max(0, Math.min(100, Math.round(lateral))),
        })),

    setPressureTrancparency: (val: number) => set({ pressureTransparency: val }),

    setLiftedInsoleTransparency: (val: number) => set({ liftedInsoleTransparency: val }),

    setShoeDataTransparency: (val: number) => set({ shoeDataTransparency: val }),

    setBonesTransparency: (val: number) => set({ bonesTransparency: val }),

    toggleInsolesVisible: () =>
        set((state) => ({ insolesVisible: !state.insolesVisible })),

    toggleGCodeVisible: () =>
        set((state) => ({ gcodeVisible: !state.gcodeVisible })),

    setTopInsoleDeafultColor: (val: string) => set({ topInsoleDefaultColor: val }),
    setBottomInsoleDeafultColor: (val: string) => set({ bottomInsoleDefaultColor: val }),
    setLiftedInsoleDefaultColor: (val: string) => set({ liftedInsoleDefaultColor: val }),
    setShoeDataDefaultColor: (val: string) => set({ shoeDataDefaultColor: val }),

    toggleAdditionsViewMeshVisible: () =>
        set((state) => ({ additionsViewMeshVisible: !state.additionsViewMeshVisible })),
    toggleLiftedInsoleMeshVisible: () =>
        set((state) => ({ liftedInsoleMeshVisible: !state.liftedInsoleMeshVisible })),
    toggleBonesMeshVisible: () =>
        set((state) => ({ bonesMeshVisible: !state.bonesMeshVisible })),
    toggleFootMeshVisible: () =>
        set((state) => ({ footMeshVisible: !state.footMeshVisible })),
    toggleShoeDataMeshVisible: () =>
        set((state) => ({ shoeDataMeshVisible: !state.shoeDataMeshVisible })),
    toggleHighHeelMeshVisible: () =>
        set((state) => ({ highHeelMeshVisible: !state.highHeelMeshVisible })),
    toggleAdvance: () => set((state) => ({ advanceMode: !state.advanceMode })),
    toggleAlignPressure: () => set((state) => ({ alignPressure: !state.alignPressure })),
    setAlignFoot: (alignFoot) => set({ alignFoot }),
    toggleAnimatePressure: () => set((state) => ({ animatePressure: !state.animatePressure })),

    setGcodeStatus: (percent: number, result: string) => set({ gcodeStatus: { percent, result } }),
    setMarkerPosition: (position: { x: string, y: string, z: string } | null) => set({ markerPosition: position }),
    setShowMarkerPosition: (val: boolean) => set({ showMarkerPosition: val }),
    toggleColoring: () => set((state) => ({ coloring: !state.coloring })),
    setColoring: (val: boolean) => set({ coloring: val }),
    setOpenCVReady: (isReady: boolean) => set({ isOpenCVReady: isReady }),
    setSelectedAdditionFromViewer: (foot: number, additionIndex: number) => set({
        selectedAdditionFromViewer: { foot, additionIndex }
    }),
    setUpdateFromViewer: (value: boolean) => set({ updateFromViewer: value }),
    setUpdateFromPanel: (value: boolean) => set({ updateFromPanel: value }),
    toggleLiveMode: () => set((state) => ({ liveMode: !state.liveMode })),
    setLiveMode: (val: boolean) => set({ liveMode: val }),
    setVoiceSessionActive: (val: boolean) => set({ voiceSessionActive: val }),
    setOldLiveMode: (val: boolean) => set({ oldLiveMode: val }),
    toggleLinkMode: () => set((state) => ({ linkMode: !state.linkMode })),
    toggleSurfaceUpdated: () => set((state) => ({ surfaceUpdated: !state.surfaceUpdated })),
    cycleWireframe: () =>
        set((state) => {
            let next: string;
            switch (state.wireframe) {
                case 'insole':
                    next = 'wireframe';
                    break;
                case 'wireframe':
                    next = 'both';
                    break;
                case 'both':
                default:
                    next = 'insole';
                    break;
            }
            return { wireframe: next };
        }),
    setWireframe: (val: string) => set({ wireframe: val }),
    cycleFeetView: () =>
        set((state) => {
            let next: ViewerStore['feetView'];
            switch (state.feetView) {
                case 'both':
                    next = 'right';
                    break;
                case 'right':
                    next = 'left';
                    break;
                case 'left':
                default:
                    next = 'both';
                    break;
            }
            return { feetView: next };
        }),
    setFeetView: (val: string) => set({ feetView: val }),
}));
